import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Users,
  UserPlus,
  CheckCircle,
  ArrowLeft,
  Calendar,
  ClipboardPaste,
  MapPin,
  Trophy,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  getEvent,
  listEventParticipants,
  listPlayerConstructs,
  listUsers,
} from "../../graphql/queries";
import {
  createEventParticipant,
  createPlayerConstruct,
  updateEventParticipant,
} from "../../graphql/mutations";
import { formatDate } from "../../utils/dateUtils";
import { ParticipantStatus } from "../../API";
import { COMMANDER_CATEGORIES, CommanderCategory } from "../../lib/commander";
import {
  matchAttendancePlayers,
  normalizeAttendanceName,
  parseEventLinkPlayerList,
} from "../../lib/eventLinkPlayerList";
import {
  getPlayerDisplayName,
  getPlayerIdentityKey,
} from "../../lib/playerIdentity";

const client = generateClient();

const EventRegistration: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedCategory, setSelectedCategory] =
    useState<CommanderCategory>("CASUAL");
  const [attendanceReport, setAttendanceReport] = useState("");
  const [attendanceCategory, setAttendanceCategory] =
    useState<CommanderCategory>("CASUAL");
  const [isImportingAttendance, setIsImportingAttendance] = useState(false);
  const [attendanceMessage, setAttendanceMessage] = useState("");

  // Fetch event details
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch current participants
  const {
    data: participants,
    isLoading: participantsLoading,
    error: participantsError,
  } = useQuery({
    queryKey: ["eventParticipants", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventParticipants,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventParticipants.items;
    },
    enabled: !!eventId,
  });

  // Fetch all users for registration
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 100 },
      });
      return result.data.listUsers.items;
    },
  });

  const {
    data: constructs,
    isLoading: constructsLoading,
    error: constructsError,
  } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  // Register user for event
  const registerUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await client.graphql({
        query: createEventParticipant,
        variables: {
          input: {
            eventId: eventId!,
            userId,
            registrationDate: new Date().toISOString(),
            status: ParticipantStatus.REGISTERED,
            commanderCategory:
              event?.format === "Commander" ? (selectedCategory as any) : null,
          },
        },
      });
      return result.data.createEventParticipant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["eventParticipants", eventId],
      });
      setSelectedUserId("");
      setSelectedCategory("CASUAL");
    },
    onError: (error: any) => {
      console.error("Error registering user:", error);
      alert("Failed to register user for event");
    },
  });

  // Update participant status
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      participantId,
      status,
    }: {
      participantId: string;
      status: ParticipantStatus;
    }) => {
      const result = await client.graphql({
        query: updateEventParticipant,
        variables: {
          input: {
            id: participantId,
            status,
          },
        },
      });
      return result.data.updateEventParticipant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["eventParticipants", eventId],
      });
    },
    onError: (error: any) => {
      console.error("Error updating participant status:", error);
      alert("Failed to update participant status");
    },
  });

  const handleRegisterUser = () => {
    if (!selectedUserId) return;
    registerUserMutation.mutate(selectedUserId);
  };

  const handleStatusChange = (
    participantId: string,
    newStatus: ParticipantStatus,
  ) => {
    updateStatusMutation.mutate({ participantId, status: newStatus });
  };

  const handleCategoryChange = async (
    participantId: string,
    commanderCategory: CommanderCategory,
  ) => {
    try {
      await client.graphql({
        query: updateEventParticipant,
        variables: {
          input: {
            id: participantId,
            commanderCategory: commanderCategory as any,
          },
        },
      });
      await queryClient.invalidateQueries({
        queryKey: ["eventParticipants", eventId],
      });
    } catch (error) {
      console.error("Error updating Commander category:", error);
      alert("Failed to update Commander category");
    }
  };

  const getStatusBadge = (status: ParticipantStatus) => {
    switch (status) {
      case ParticipantStatus.REGISTERED:
        return <Badge variant="secondary">Registered</Badge>;
      case ParticipantStatus.CHECKED_IN:
        return <Badge variant="default">Checked In</Badge>;
      case ParticipantStatus.DROPPED:
        return <Badge variant="destructive">Dropped</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusOptions = (currentStatus: ParticipantStatus) => {
    const options = [
      ParticipantStatus.REGISTERED,
      ParticipantStatus.CHECKED_IN,
      ...(event?.format === "Commander" ? [ParticipantStatus.DROPPED] : []),
    ];
    return options.filter((option) => option !== currentStatus);
  };

  // Get user details for participants
  const getUserDetails = (userId: string) => {
    return users?.find((user: any) => user.id === userId);
  };

  // Get available users (not already registered)
  const getAvailableUsers = () => {
    if (!users || !participants) return [];
    const registeredUserIds = participants.map((p: any) => p.userId);
    return users.filter((user: any) => !registeredUserIds.includes(user.id));
  };

  const parsedAttendance = useMemo(
    () => parseEventLinkPlayerList(attendanceReport),
    [attendanceReport],
  );
  const attendanceMatches = useMemo(
    () =>
      parsedAttendance && users
        ? matchAttendancePlayers(parsedAttendance.playerNames, users)
        : [],
    [parsedAttendance, users],
  );
  const matchedAttendance = attendanceMatches.filter(
    (match) => match.status === "matched",
  );
  const findConstructForImportedName = (importedName: string) => {
    const normalizedName = normalizeAttendanceName(
      importedName.replace(/\.{3}$/, ""),
    );
    return constructs?.find(
      (construct: any) =>
        normalizeAttendanceName(
          construct.normalizedName || construct.name,
        ) === normalizedName,
    );
  };
  const attendanceConstructMatches = attendanceMatches.filter(
    (match) =>
      match.status !== "matched" &&
      !!findConstructForImportedName(match.importedName),
  );
  const attendanceConstructCreates =
    attendanceMatches.length -
    matchedAttendance.length -
    attendanceConstructMatches.length;

  const importAttendance = async () => {
    if (!parsedAttendance || !eventId || !participants || !constructs) return;

    setIsImportingAttendance(true);
    setAttendanceMessage("");
    try {
      let importedCount = 0;
      const resolvedConstructs = [...constructs];
      for (const match of attendanceMatches) {
        let identity: { userId?: string; playerConstructId?: string };
        if (match.status === "matched") {
          identity = { userId: match.users[0].id };
        } else {
          const normalizedName = normalizeAttendanceName(
            match.importedName.replace(/\.{3}$/, ""),
          );
          let construct = resolvedConstructs.find(
            (candidate: any) =>
              normalizeAttendanceName(
                candidate.normalizedName || candidate.name,
              ) === normalizedName,
          );
          if (!construct) {
            const response: any = await client.graphql({
              query: createPlayerConstruct,
              variables: {
                input: {
                  name: match.importedName,
                  normalizedName,
                  totalEvents: 0,
                  totalPoints: 0,
                  totalWins: 0,
                  totalLosses: 0,
                  totalDraws: 0,
                },
              },
            });
            construct = response.data.createPlayerConstruct;
            if (!construct) {
              throw new Error(
                `Unable to create a player construct for ${match.importedName}.`,
              );
            }
            resolvedConstructs.push(construct);
          }
          identity = { playerConstructId: construct.id };
        }

        const identityKey = getPlayerIdentityKey(identity);
        const existingParticipant = participants.find(
          (participant: any) =>
            getPlayerIdentityKey(participant) === identityKey,
        );
        if (existingParticipant) {
          await client.graphql({
            query: updateEventParticipant,
            variables: {
              input: {
                id: existingParticipant.id,
                status: ParticipantStatus.CHECKED_IN,
                commanderCategory:
                  existingParticipant.commanderCategory ||
                  (attendanceCategory as any),
              },
            },
          });
        } else {
          await client.graphql({
            query: createEventParticipant,
            variables: {
              input: {
                eventId,
                ...identity,
                registrationDate: new Date().toISOString(),
                status: ParticipantStatus.CHECKED_IN,
                commanderCategory: attendanceCategory as any,
              },
            } as any,
          });
        }
        importedCount += 1;
      }
      await queryClient.invalidateQueries({
        queryKey: ["eventParticipants", eventId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["allPlayerConstructs"],
      });
      setAttendanceMessage(
        `Checked in all ${importedCount} imported players. Players without site accounts were saved as player constructs and can be linked later.`,
      );
    } catch (error) {
      console.error("Error importing EventLink attendance:", error);
      setAttendanceMessage(
        "The import stopped after an error. Successfully processed players remain checked in; review the participant list before retrying.",
      );
    } finally {
      setIsImportingAttendance(false);
    }
  };

  if (
    eventLoading ||
    participantsLoading ||
    usersLoading ||
    constructsLoading
  ) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (
    eventError ||
    participantsError ||
    usersError ||
    constructsError ||
    !event
  ) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading event registration data
        </div>
      </div>
    );
  }

  const availableUsers = getAvailableUsers();

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/events/${eventId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Event
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Event Registration</h1>
          <p className="text-muted-foreground mt-2">
            Manage participants for {event.name}
          </p>
        </div>
      </div>

      {event.format === "Commander" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" />
              Import EventLink Attendance
            </CardTitle>
            <CardDescription>
              Paste an EventLink Player List report. Every listed player will be
              checked in; names without accounts are saved as player constructs
              that can be linked later.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={attendanceReport}
              onChange={(event) => {
                setAttendanceReport(event.target.value);
                setAttendanceMessage("");
              }}
              className="min-h-52 font-mono text-xs"
              placeholder="Paste the complete EventLink Player List report here..."
            />
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-52 space-y-2">
                <label className="text-sm font-medium">
                  Default imported category
                </label>
                <Select
                  value={attendanceCategory}
                  onValueChange={(value) =>
                    setAttendanceCategory(value as CommanderCategory)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMANDER_CATEGORIES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={importAttendance}
                disabled={
                  !parsedAttendance ||
                  isImportingAttendance
                }
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {isImportingAttendance
                  ? "Importing..."
                  : `Check In All ${attendanceMatches.length}`}
              </Button>
            </div>

            {attendanceReport && !parsedAttendance && (
              <p className="text-sm text-destructive">
                This does not look like an EventLink Player List report. Include
                the report header, Player/Table header, and dashed separator.
              </p>
            )}

            {parsedAttendance && (
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-white/10 p-3">
                  <p className="text-2xl font-semibold text-green-400">
                    {matchedAttendance.length}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Matched accounts
                  </p>
                </div>
                <div className="rounded-lg border border-white/10 p-3">
                  <p className="text-2xl font-semibold text-amber-400">
                    {attendanceConstructMatches.length}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Existing constructs
                  </p>
                </div>
                <div className="rounded-lg border border-white/10 p-3">
                  <p className="text-2xl font-semibold text-primary">
                    {attendanceConstructCreates}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    New constructs
                  </p>
                </div>
              </div>
            )}

            {attendanceConstructCreates > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
                <p className="font-medium">
                  {attendanceConstructCreates} player construct
                  {attendanceConstructCreates === 1 ? "" : "s"} will be created
                </p>
                <p className="mt-1 text-muted-foreground">
                  These players can participate in pods immediately and can be
                  linked to site accounts later.
                </p>
              </div>
            )}
            {attendanceMessage && (
              <p className="text-sm text-muted-foreground">
                {attendanceMessage}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Event Details */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
              <CardDescription>Event information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4" />
                <span>{event.location}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Trophy className="h-4 w-4" />
                <span>{event.format}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4" />
                <span>{participants?.length || 0} participants</span>
              </div>
            </CardContent>
          </Card>

          {/* Add Participant */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Add Participant</CardTitle>
              <CardDescription>Register a user for this event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user: any) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.displayName} ({user.username})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {event.format === "Commander" && (
                <Select
                  value={selectedCategory}
                  onValueChange={(value) =>
                    setSelectedCategory(value as CommanderCategory)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Commander category" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMANDER_CATEGORIES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                onClick={handleRegisterUser}
                disabled={!selectedUserId || registerUserMutation.isPending}
                className="w-full"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {registerUserMutation.isPending
                  ? "Registering..."
                  : "Register User"}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Participants List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Participants</CardTitle>
              <CardDescription>
                Manage event participants and their status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {participants && participants.length > 0 ? (
                <div className="space-y-4">
                  {participants.map((participant: any) => {
                    const user = getUserDetails(participant.userId);
                    const displayName = getPlayerDisplayName(
                      participant,
                      users || [],
                      constructs || [],
                    );
                    return (
                      <div
                        key={participant.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium">
                            {displayName}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {user?.username ||
                              (participant.playerConstructId
                                ? "Player construct"
                                : "Unlinked player")}{" "}
                            • Registered{" "}
                            {formatDate(participant.registrationDate)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {event.format === "Commander" && (
                            <Select
                              value={participant.commanderCategory || undefined}
                              onValueChange={(value) =>
                                handleCategoryChange(
                                  participant.id,
                                  value as CommanderCategory,
                                )
                              }
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue placeholder="Set category" />
                              </SelectTrigger>
                              <SelectContent>
                                {COMMANDER_CATEGORIES.map(
                                  ({ value, label }) => (
                                    <SelectItem key={value} value={value}>
                                      {label}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                          )}
                          {getStatusBadge(participant.status)}
                          <Select
                            value={participant.status}
                            onValueChange={(newStatus) =>
                              handleStatusChange(
                                participant.id,
                                newStatus as ParticipantStatus,
                              )
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getStatusOptions(participant.status).map(
                                (status) => (
                                  <SelectItem key={status} value={status}>
                                    {status.replace("_", " ")}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    No Participants
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    No users have registered for this event yet.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventRegistration;
